import { Execution } from "@/shared/Executions/models/Execution";
import { ExecutionEngineType } from "@/shared/Executions/models/ExecutionEngineType";
import { ExecutionEngineVersion } from "@/shared/Executions/models/ExecutionEngineVersion";
import { ExecutionPhase, ExecutionPhaseOutput } from "@/shared/Executions/models/ExecutionPhase";
import { AdvancedAnalysisPhaseType, ExecutionPhaseType } from "@/shared/Executions/models/ExecutionPhaseType";
import { InjectableService } from "@/shared/services/InjectableService";


export interface IExecutionPhaseService {

    tryGetExecutionPhaseForPhaseType: (execution: Execution, phaseType: ExecutionPhaseType) => ExecutionPhase | undefined;
    tryGetOutputForPhaseType: (execution: Execution, phaseType: ExecutionPhaseType) => ExecutionPhaseOutput | undefined;
    tryGetVersionforEnginePhaseByType: (engineType: ExecutionEngineType, phase?: ExecutionPhase) => string;
    tryGetPrimaryPhaseTypeForExecution: (execution: Execution) => ExecutionPhaseType | typeof AdvancedAnalysisPhaseType | undefined;
    getExecutionEngineVersionsForExecution: (execution: Execution) => Array<ExecutionEngineVersion>
}


export const VISUALIZATION_PHASE_TYPES = new Set<ExecutionPhaseType>([
    ExecutionPhaseType.Visualization,
    ExecutionPhaseType.CustomPlots,
    ExecutionPhaseType.CustomTables
]);


export class ExecutionPhaseService extends InjectableService implements IExecutionPhaseService {

    private readonly _defaultVersionDisplay = "Not Available";

    static readonly injectableKey = 'IExecutionPhaseService';

    get injectableKey() {
        return (this.constructor as typeof ExecutionPhaseService).injectableKey;
    }

    public tryGetExecutionPhaseForPhaseType = (execution: Execution, phaseType: ExecutionPhaseType | typeof AdvancedAnalysisPhaseType): ExecutionPhase | undefined => {
        if (!execution?.phases?.length) return;

        // If requesting Visualization, return the first matching visualization-family phase
        if (phaseType === ExecutionPhaseType.Visualization) {
            return execution.phases.find(x => x.phase && VISUALIZATION_PHASE_TYPES.has(x.phase));
        }

        // Otherwise, match by exact phaseType
        return execution.phases.find(x => x.phase === phaseType);
    }

    public tryGetOutputForPhaseType = (execution: Execution, phaseType: ExecutionPhaseType): ExecutionPhaseOutput | undefined => {

        if (!execution?.phases?.length) return;

        const phase = this.tryGetExecutionPhaseForPhaseType(execution, phaseType);
        return phase?.outputs?.find(x => x.phase === phaseType);

    }

    public tryGetVersionforEnginePhaseByType = (engineType: ExecutionEngineType, phase?: ExecutionPhase): string => {

        let version: string | undefined = this._defaultVersionDisplay;
        version = phase?.versions?.find(x => x.package === engineType)?.version;
        return version ?? this._defaultVersionDisplay;

    }

    public tryGetPrimaryPhaseTypeForExecution = (execution: Execution): ExecutionPhaseType | typeof AdvancedAnalysisPhaseType | undefined => {
        if (!execution) return;

        // Group phases: treat all visualization-family as one group
        const phaseTypes = (execution?.phases ?? []).map(x => x.phase).filter(Boolean) as ExecutionPhaseType[];
        const uniqueGroups = new Set<string>();
        for (const phase of phaseTypes) {
            if (VISUALIZATION_PHASE_TYPES.has(phase)) {
                uniqueGroups.add('visualization');
            } else {
                uniqueGroups.add(phase);
            }
        }

        const containsDataCleaningPhase = phaseTypes.includes(ExecutionPhaseType.DataCleaning);
        const containsNcaAnalysisPhase = phaseTypes.includes(ExecutionPhaseType.NCAAnalysis);
        const containsCustomCalculationsPhase = phaseTypes.includes(ExecutionPhaseType.CustomCalculations);
        const containsVisualizationPhase = phaseTypes.some(p => VISUALIZATION_PHASE_TYPES.has(p));
        const containsReportsPhase = phaseTypes.includes(ExecutionPhaseType.Reports);

        let primaryExecutionPhaseType: ExecutionPhaseType | typeof AdvancedAnalysisPhaseType | undefined = undefined;

        if (uniqueGroups.size > 1) {
            primaryExecutionPhaseType = AdvancedAnalysisPhaseType;
        } else if (containsReportsPhase) {
            primaryExecutionPhaseType = ExecutionPhaseType.Reports;
        } else if (containsVisualizationPhase) {
            primaryExecutionPhaseType = ExecutionPhaseType.Visualization;
        } else if (containsCustomCalculationsPhase) {
            primaryExecutionPhaseType = ExecutionPhaseType.CustomCalculations;
        } else if (containsNcaAnalysisPhase) {
            primaryExecutionPhaseType = ExecutionPhaseType.NCAAnalysis;
        } else if (containsDataCleaningPhase) {
            primaryExecutionPhaseType = ExecutionPhaseType.DataCleaning;
        }

        return primaryExecutionPhaseType;
    }

    public getExecutionEngineVersionsForExecution = (execution: Execution): Array<ExecutionEngineVersion> => {

        let engineVersions: Array<ExecutionEngineVersion> = [...(execution?.engineVersions ?? [])];
        return engineVersions;

    }


}
